import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, FileText, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicPolicyPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Derive type from URL path: /agb or /datenschutz
  const type = location.pathname === '/agb' ? 'agb' : 'datenschutz';
  
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPolicy();
  }, [type]);

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Map URL type to API endpoint
      const endpoint = type === 'agb' ? 'terms' : 'privacy';
      
      const response = await fetch(`${BACKEND_URL}/api/policies/${endpoint}`);
      
      if (response.ok) {
        const data = await response.json();
        setPolicy(data);
      } else {
        setError('Dokument konnte nicht geladen werden');
      }
    } catch (err) {
      console.error('Error fetching policy:', err);
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    return type === 'agb' ? 'Allgemeine Geschäftsbedingungen (AGB)' : 'Datenschutzerklärung';
  };

  const getIcon = () => {
    return type === 'agb' 
      ? <FileText className="h-8 w-8 text-[#C9A227]" /> 
      : <Shield className="h-8 w-8 text-[#C9A227]" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A227] mx-auto mb-4"></div>
          <p className="text-gray-400">Wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="w-full max-w-lg text-center bg-zinc-900 border-zinc-800">
          <CardContent className="pt-8 pb-8">
            <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Fehler</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Button
              onClick={fetchPolicy}
              variant="outline"
              className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-black"
              data-testid="public-policy-retry-button"
            >
              Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-zinc-900 shadow-lg border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-xl p-1.5 shadow-sm">
              <img
                src="/assets/invictafit-logo.png"
                alt="InvictaFit"
                className="h-9 w-auto object-contain"
              />
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/portal')}
            className="text-gray-400 hover:text-white hover:bg-zinc-800"
            data-testid="public-policy-back-to-portal"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="shadow-xl bg-zinc-900 border-zinc-800">
          <CardHeader className="border-b border-zinc-700 bg-zinc-800/50">
            <div className="flex items-center gap-3">
              {getIcon()}
              <div>
                <CardTitle className="text-2xl text-white">{getTitle()}</CardTitle>
                {policy?.version && (
                  <p className="text-sm text-gray-400 mt-1">
                    Version {policy.version} • Stand: {policy.effective_date ? new Date(policy.effective_date).toLocaleDateString('de-CH') : 'Aktuell'}
                  </p>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 md:p-8">
            {/* Render policy content */}
            <div className="prose prose-invert max-w-none">
              {policy?.content ? (
                <div className="whitespace-pre-wrap text-gray-300 leading-relaxed">
                  {policy.content}
                </div>
              ) : (
                <p className="text-gray-500 italic">Kein Inhalt verfügbar</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <div className="flex justify-center gap-4">
            {type !== 'datenschutz' && (
              <Link to="/datenschutz" className="text-[#C9A227] hover:underline" data-testid="public-policy-link-datenschutz">
                Datenschutzerklärung
              </Link>
            )}
            {type !== 'agb' && (
              <Link to="/agb" className="text-[#C9A227] hover:underline" data-testid="public-policy-link-agb">
                Allgemeine Geschäftsbedingungen
              </Link>
            )}
          </div>
          <div className="mt-4 flex justify-center">
            <Link to="/portal" data-testid="public-policy-portal-button-link">
              <Button className="bg-[#C9A227] text-black hover:bg-[#d8b02b]" data-testid="public-policy-portal-button">
                Zum Kundenportal
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-8 pb-6 text-center text-gray-600 text-sm">
        <p>© {new Date().getFullYear()} InvictaFit - Alle Rechte vorbehalten</p>
      </footer>
    </div>
  );
};

export default PublicPolicyPage;
